function _interopDefault(ex) {
  return ex && typeof ex === 'object' && 'default' in ex ? ex['default'] : ex;
}

var moment = _interopDefault(require('moment'));

var namedUrls = require('named-urls');

var useReactRouter = _interopDefault(require('use-react-router'));

var React = require('react');

var reactRouter = require('react-router');
/**
 * Maps an API map of ConfigurationProperty objects to
 * an autoform IFormDefinition object.  Use on properties
 * objects from backend responses to ensure they're mapped
 * properly
 *
 * @param properties
 */


function objectWithoutProperties(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

function toFormDefinition(properties) {
  if (!properties) {
    throw new Error('Undefined value passed to form definition converter');
  }

  var answer = {};
  Object.keys(properties).forEach(function (key) {
    answer[key] = toFormDefinitionProperty(properties[key]);
  });
  return answer;
}
/**
 * Maps an API ConfigurationProperty object to an autoform IFormDefinitionPropertyObject
 * @param property
 */


function toFormDefinitionProperty(property) {
  var cols = property.cols;
  var max = property.max;
  var min = property.min;
  var multiple = property.multiple;
  var rows = property.rows;
  var controlHint = property.controlHint;
  var controlTooltip = property.controlTooltip;
  var labelHint = property.labelHint;
  var labelTooltip = property.labelTooltip;
  var rest = objectWithoutProperties(property, ["cols", "max", "min", "multiple", "rows", "controlHint", "controlTooltip", "labelHint", "labelTooltip"]);
  var formDefinitionProperty = rest; // needed, ConfigurationProperty is a lie

  return Object.assign({}, formDefinitionProperty, {
    controlHint: controlHint || controlTooltip,
    fieldAttributes: {
      cols: cols,
      max: max,
      min: min,
      multiple: multiple,
      rows: rows
    },
    labelHint: labelHint || labelTooltip
  });
}

function anyFieldsRequired(properties) {
  return Object.keys(properties).filter(function (key) {
    return requiredTypeMask(properties[key].type);
  }).filter(function (key) {
    return properties[key].required;
  }).length > 0;
}

function requiredTypeMask(type) {
  switch (type) {
    case 'boolean':
    case 'checkbox':
    case 'hidden':
      return false;

    default:
      return true;
  }
}

function allFieldsRequired(properties) {
  var keys = Object.keys(properties).filter(function (key) {
    return requiredTypeMask(properties[key].type);
  });
  var allRequired = keys.filter(function (key) {
    return properties[key].required;
  });

  if (allRequired.length === 0) {
    return false;
  }

  return keys.length === allRequired.length;
}

function getRequiredStatusText(properties, allRequired, someRequired, noneRequired) {
  if (allFieldsRequired(properties)) {
    return allRequired;
  }

  if (anyFieldsRequired(properties)) {
    return someRequired;
  }

  return noneRequired;
}
/**
 * Evaluates the values according to the given property definition and returns
 * a boolean if the supplied values are valid or not.
 * @param properties
 * @param values
 */


function validateConfiguredProperties(properties, values) {
  if (typeof values === 'undefined') {
    return false;
  }

  var allRequired = Object.keys(properties).filter(function (key) {
    return properties[key].required;
  });

  if (allRequired.length === 0) {
    return true;
  }

  var allRequiredSet = allRequired.map(function (key) {
    return validateRequired(values[key]);
  }).reduce(function (prev, curr) {
    return curr;
  }, false);
  return allRequiredSet;
}
/**
 * Examine the given property and determine if it's set or not,
 * for string values this includes evaluating against ''
 * @param value
 */


function validateRequired(value) {
  if (typeof value === 'undefined') {
    return false;
  }

  if (typeof value === 'string') {
    return value !== '';
  }

  return true;
}
/**
 * Evaluates the given values against the supplied property definition
 * object and returns an IFormErrors map that can be returned to auto-form
 * @param definition
 * @param getErrorString
 * @param values
 */


function validateRequiredProperties(definition, getErrorString, values, prefix) {
  if (prefix === void 0) prefix = '';
  var allRequired = Object.keys(definition).filter(function (key) {
    return requiredTypeMask(definition[key].type);
  }).filter(function (key) {
    return definition[key].required;
  });

  if (allRequired.length === 0) {
    return {};
  }

  var sanitizedValues = values || {};
  var validationResults = allRequired.map(function (key) {
    return {
      key: key,
      defined: validateRequired(sanitizedValues[key])
    };
  }).reduce(function (acc, current) {
    if (!current.defined) {
      acc["" + prefix + current.key] = getErrorString(definition[current.key].displayName || current.key);
    }

    return acc;
  }, {});
  var arrayValidationResults = allRequired.filter(function (key) {
    return definition[key].type === 'array';
  }).reduce(function (acc, current) {
    var arrayValue = sanitizedValues[current] || [];
    var arrayDefinition = definition[current].arrayDefinition;
    var result = arrayValue.map(function (value, index) {
      return validateRequiredProperties(arrayDefinition, getErrorString, value, current + "[" + index + "].");
    }).reduce(function (accInner, currentInner) {
      return Object.assign({}, accInner, currentInner);
    }, {});
    return Object.assign({}, acc, result);
  }, {});
  return Object.assign({}, validationResults, arrayValidationResults);
}

function toDurationDifferenceString(timestamp, defaultValue) {
  if (defaultValue === void 0) defaultValue = 'n/a';

  if (!timestamp) {
    return defaultValue;
  }

  var startDate = moment(timestamp);
  var uptimeDuration = moment.duration(moment().diff(startDate));
  var duration = {
    days: uptimeDuration.days(),
    hours: uptimeDuration.hours(),
    minutes: uptimeDuration.minutes()
  };
  var durationString = Object.keys(duration).reduce(function (timeSpan, key) {
    return duration[key] > 0 ? timeSpan + duration[key] + " " + key + " " : timeSpan;
  }, '');
  return durationString && durationString.length > 0 ? durationString : defaultValue;
}

function toDurationString(timeDuration, unit) {
  if (!timeDuration) {
    return 'NaN';
  }

  if (unit === 'ns') {
    timeDuration = timeDuration / 1000000;
  }

  var durationMoment = moment.duration(timeDuration);
  var days = Math.floor(durationMoment.days());
  var hours = Math.floor(durationMoment.hours());
  var minutes = Math.floor(durationMoment.minutes());
  var seconds = Math.floor(durationMoment.seconds());
  var milliseconds = Math.floor(durationMoment.milliseconds());
  var durationStrings = [];

  if (days > 0) {
    durationStrings.push(days + " days");
  }

  if (hours > 0) {
    durationStrings.push(hours + " hours");
  }

  if (minutes > 0) {
    durationStrings.push(minutes + " minutes");
  }

  if (seconds > 0) {
    durationStrings.push(seconds + " seconds");
  }

  if (durationStrings.length === 0) {
    if (milliseconds > 0) {
      durationStrings.push(milliseconds + " ms");
    } else if (timeDuration !== 0) {
      durationStrings.push(timeDuration.toFixed(2) + " ms");
    }
  }

  return durationStrings.join(', ').trim();
}
/***
 *
 * @param timestamp - Date to be formatted
 * @return string - Formatted date as, for example, Jan 1st 23:42
 */


function toShortDateAndTimeString(timestamp) {
  if (!timestamp) {
    return 'NaN';
  }

  return moment(timestamp).format('MMM Do HH:mm');
}
/**
 * Simple debounce function that will only execute the given function
 * after waiting the specified time period
 *
 * @param func
 * @param wait
 * @param immediate
 */


function debounce(func, wait, immediate) {
  var timeout;
  return function () {
    var later = function later() {
      timeout = null;

      if (!immediate) {
        func();
      }
    };

    var callNow = immediate && typeof timeout !== 'undefined';

    if (typeof timeout !== 'undefined') {
      window.clearTimeout(timeout);
    }

    timeout = window.setTimeout(later, wait);

    if (callNow) {
      func();
    }
  };
}

var ALPHABET = '-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz';
var RANDOMNESS = [];
var lastTimestamp = 0;
/**
 * Generates lexically sortable unique keys based on:
 *
 * https://firebase.googleblog.com/2015/02/the-2120-ways-to-ensure-unique_68.html
 *
 * You can also consider the generated kys to be like UUIDS except:
 * (1) strictly increment from the generating node's point of view
 * (2) loosely increment based on relative machine time when viewed across nodes.
 */

function key() {
  // first time setup.. initialize the randomness...
  if (RANDOMNESS.length === 0) {
    for (var i = 0; i < 12; i++) {
      RANDOMNESS[i] = Math.floor(Math.random() * 64);
    }
  } // we build the resulting key backwards. The most significant bits at the front of the key.


  var result = '';
  var timestamp = new Date().getTime(); // Lets encode the random part of the key. (72 bits of randomness)
  // 72/6 = 12 base64 characters.
  // increment randomness when we being called too keys too quickly.

  if (timestamp === lastTimestamp) {
    for (var i$1 = 0; i$1 < 12; i$1++) {
      RANDOMNESS[i$1]++;

      if (RANDOMNESS[i$1] === 64) {
        RANDOMNESS[i$1] = 0; // we need to carry to the next random byte.
      } else {
          break; // done incrementing.
        }
    }
  }

  for (var i$2 = 0; i$2 < 12; i$2++) {
    result = ALPHABET.charAt(RANDOMNESS[i$2]) + result;
  } // Base64 encodes 6 bits of data per character. We want to encode
  // 6 bytes of the timestamp (48 bits), 48/6 = 8 base64 chars for the ts part
  // the key.


  var remainingTimestamp = timestamp;

  for (var i$3 = 0; i$3 < 8; i$3++) {
    result = ALPHABET.charAt(remainingTimestamp % 64) + result;
    remainingTimestamp = Math.floor(remainingTimestamp / 64);
  }

  lastTimestamp = timestamp;
  return result;
}
/**
 * Creates a function that takes a route and some `data` `T` that returns the
 * reversed URL.
 * Use `mapper` to write the business logic required to convert the `data` object
 * to the basic params that can be passed in an url (strings and numbers), and to
 * set the state object that will be pushed in the history together with the url.
 * @param defaultRoute
 * @param mapper
 */


function makeResolver(defaultRoute, mapper) {
  return function (data) {
    var ref = mapper(data);
    var params = ref.params;
    var route = ref.route;
    var state = ref.state;
    return {
      params: params,
      pathname: namedUrls.reverse(route || defaultRoute, params || {}),
      route: route || defaultRoute,
      state: state
    };
  };
}
/**
 * Creates a function that takes a route and some `data` `T` that returns the
 * reversed URL.
 * Use `mapper` to write the business logic required to convert the `data` object
 * to the basic params that can be passed in an url (strings and numbers), and to
 * set the state object that will be pushed in the history together with the url.
 * @param defaultRoute
 * @param mapper
 */


function makeResolverNoParamsWithDefaults(defaultRoute, mapper) {
  return function () {
    var ref = mapper();
    var params = ref.params;
    var route = ref.route;
    var state = ref.state;
    return {
      params: params,
      pathname: namedUrls.reverse(route || defaultRoute, params || {}),
      route: route || defaultRoute,
      state: state
    };
  };
}
/**
 * Creates a function that takes a route and some `data` `T` that returns the
 * reversed URL.
 * @param route
 */


function makeResolverNoParams(route) {
  return function () {
    return {
      pathname: namedUrls.reverse(route),
      route: route
    };
  };
}

var WithListViewToolbarHelpers =
/*@__PURE__*/
function (superclass) {
  function WithListViewToolbarHelpers(props) {
    var this$1 = this;
    superclass.call(this, props);

    this.onUpdateCurrentValue = function (event) {
      this$1.setState({
        currentValue: event.target.value
      });
    };

    this.onValueKeyPress = function (keyEvent) {
      var ref = this$1.state;
      var currentValue = ref.currentValue;
      var currentFilterType = ref.currentFilterType;

      if (keyEvent.key === 'Enter' && currentValue && currentValue.length > 0) {
        this$1.setState({
          currentValue: ''
        });
        this$1.onFilterAdded(currentFilterType.id, currentFilterType.title, currentValue);
        keyEvent.stopPropagation();
        keyEvent.preventDefault();
      }
    };

    this.onFilterAdded = function (id, title, value) {
      var ref = this$1.state;
      var activeFilters = ref.activeFilters;
      this$1.setState({
        activeFilters: activeFilters.concat([{
          id: id,
          title: title,
          value: value
        }])
      });
    };

    this.onSelectFilterType = function (filterType) {
      var ref = this$1.state;
      var currentFilterType = ref.currentFilterType;

      if (currentFilterType !== filterType) {
        this$1.setState({
          currentValue: '',
          currentFilterType: filterType
        });
      }
    };

    this.onFilterValueSelected = function (filterValue) {
      var ref = this$1.state;
      var currentFilterType = ref.currentFilterType;
      this$1.setState({
        currentValue: filterValue.title
      });

      if (filterValue) {
        this$1.onFilterAdded(currentFilterType.id, currentFilterType.title, filterValue.title);
      }
    };

    this.onRemoveFilter = function (filter) {
      var ref = this$1.state;
      var activeFilters = ref.activeFilters;
      var index = activeFilters.indexOf(filter);

      if (index > -1) {
        var updated = activeFilters.slice(0, index).concat(activeFilters.slice(index + 1));
        this$1.setState({
          activeFilters: updated
        });
      }
    };

    this.onClearFilters = function (event) {
      event.preventDefault();
      this$1.setState({
        activeFilters: []
      });
    };

    this.onToggleCurrentSortDirection = function () {
      var ref = this$1.state;
      var isSortAscending = ref.isSortAscending;
      this$1.setState({
        isSortAscending: !isSortAscending
      });
    };

    this.onUpdateCurrentSortType = function (sortType) {
      var ref = this$1.state;
      var currentSortType = ref.currentSortType;

      if (currentSortType.id !== sortType.id) {
        this$1.setState({
          currentSortType: sortType,
          isSortAscending: true
        });
      }
    };

    this.state = {
      activeFilters: [],
      currentFilterType: this.props.defaultFilterType,
      currentSortType: this.props.defaultSortType,
      currentValue: '',
      filterCategory: null,
      isSortAscending: this.props.defaultSortAscending
    };
  }

  if (superclass) WithListViewToolbarHelpers.__proto__ = superclass;
  WithListViewToolbarHelpers.prototype = Object.create(superclass && superclass.prototype);
  WithListViewToolbarHelpers.prototype.constructor = WithListViewToolbarHelpers;

  WithListViewToolbarHelpers.prototype.render = function render() {
    return this.props.children(Object.assign({}, {
      onClearFilters: this.onClearFilters,
      onFilterAdded: this.onFilterAdded,
      onFilterValueSelected: this.onFilterValueSelected,
      onRemoveFilter: this.onRemoveFilter,
      onSelectFilterType: this.onSelectFilterType,
      onToggleCurrentSortDirection: this.onToggleCurrentSortDirection,
      onUpdateCurrentSortType: this.onUpdateCurrentSortType,
      onUpdateCurrentValue: this.onUpdateCurrentValue,
      onValueKeyPress: this.onValueKeyPress
    }, this.state));
  };

  return WithListViewToolbarHelpers;
}(React.Component);

WithListViewToolbarHelpers.defaultProps = {
  defaultSortAscending: true
};
/**
 *
 * @param value the value being used
 * @returns the `OptionalInt` object created from the specified value
 */

function createOptionalInt(value) {
  return {
    present: true,
    asInt: value
  };
}
/**
 * @param optional the `OptionalInt` whose value is being requested
 * @param defaultValue the value to return if no value exists
 * @returns the int value (defaults to zero)
 */


function optionalIntValue(optional, defaultValue) {
  if (defaultValue === void 0) defaultValue = 0;

  if (optional && optional.present) {
    var value = optional.asInt;
    return value ? value : defaultValue;
  }

  return defaultValue;
}
/**
 * @param optional the value whose value is being checked
 * @returns true if the optional exists and there is a value
 */


function optionalIntHasValue(optional) {
  return optional && optional.present ? optional.present : false;
}

function useRouteData() {
  var ref = useReactRouter();
  var location = ref.location;
  var match = ref.match;
  var history = ref.history;
  return {
    history: history,
    location: location,
    match: match,
    params: match.params || {},
    state: location.state || {}
  };
}
/**
 * A component to handle asynchronous data-loading. It will show the provided
 * loader component for a minimum amount of time - to avoid flash of loading
 * component - before invoking the children render prop.
 * It will also handle the error state rendering the provided error component
 * in case of errors loading the data.
 *
 * @see [error]{@link IWithLoaderProps#error}
 * @see [loading]{@link IWithLoaderProps#loading}
 * @see [minWait]{@link IWithLoaderProps#minWait}
 * @see [loaderChildren]{@link IWithLoaderProps#loaderChildren}
 * @see [errorChildren]{@link IWithLoaderProps#errorChildren}
 * @see [children]{@link IWithLoaderProps#children}
 */


var WithLoader =
/*@__PURE__*/
function (superclass) {
  function WithLoader(props) {
    superclass.call(this, props);
    this.state = {
      loaded: !this.props.loading
    };
  }

  if (superclass) WithLoader.__proto__ = superclass;
  WithLoader.prototype = Object.create(superclass && superclass.prototype);
  WithLoader.prototype.constructor = WithLoader;

  WithLoader.prototype.componentWillReceiveProps = function componentWillReceiveProps(nextProps) {
    this.setState({
      loaded: !nextProps.loading
    });
  };

  WithLoader.prototype.componentDidUpdate = function componentDidUpdate(prevProps) {
    if (!this.props.loading && !this.waitTimeout) {
      this.setTimeout();
    }
  };

  WithLoader.prototype.render = function render() {
    if (this.props.error) {
      return this.props.errorChildren;
    }

    if (!this.state.loaded) {
      return this.props.loaderChildren;
    }

    return this.props.children();
  };

  WithLoader.prototype.setTimeout = function setTimeout() {
    var this$1 = this;
    this.clearTimeout();
    this.waitTimeout = window.setTimeout(function () {
      this$1.setState({
        loaded: true
      });
    }, this.props.minWait);
  };

  WithLoader.prototype.clearTimeout = function clearTimeout$1() {
    if (this.waitTimeout) {
      clearTimeout(this.waitTimeout);
      this.waitTimeout = undefined;
    }
  };

  return WithLoader;
}(React.PureComponent);

WithLoader.defaultProps = {
  minWait: 500
};
/**
 * A render-prop component that returns a route params - with type `P` - and
 * state - with type `S`.
 * *Warning:* this component doesn't provide any assurance on the returned shape
 * of neither the params nor the state objects.
 * @see [children]{@link IWithRouteDataProps#children}
 */

var WithRouteData =
/*@__PURE__*/
function (superclass) {
  function WithRouteData() {
    superclass.apply(this, arguments);
  }

  if (superclass) WithRouteData.__proto__ = superclass;
  WithRouteData.prototype = Object.create(superclass && superclass.prototype);
  WithRouteData.prototype.constructor = WithRouteData;

  WithRouteData.prototype.render = function render() {
    var this$1 = this;
    return React.createElement(reactRouter.Route, null, function (route) {
      if (!route.match) {
        throw new Error("Route doesn't have a match");
      }

      var params = route.match.params || {};
      var state = route.location.state || {};
      return this$1.props.children(params, state, route);
    });
  };

  return WithRouteData;
}(React.Component);

function objectWithoutProperties$1(obj, exclude) {
  var target = {};

  for (var k in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k];
  }

  return target;
}

var WithRouterBase =
/*@__PURE__*/
function (superclass) {
  function WithRouterBase() {
    superclass.apply(this, arguments);
  }

  if (superclass) WithRouterBase.__proto__ = superclass;
  WithRouterBase.prototype = Object.create(superclass && superclass.prototype);
  WithRouterBase.prototype.constructor = WithRouterBase;

  WithRouterBase.prototype.render = function render() {
    var ref = this.props;
    var children = ref.children;
    var rest = objectWithoutProperties$1(ref, ["children"]);
    var props = rest;
    return children(props);
  };

  return WithRouterBase;
}(React.Component);

var WithRouter = reactRouter.withRouter(WithRouterBase);
exports.toFormDefinition = toFormDefinition;
exports.toFormDefinitionProperty = toFormDefinitionProperty;
exports.anyFieldsRequired = anyFieldsRequired;
exports.allFieldsRequired = allFieldsRequired;
exports.getRequiredStatusText = getRequiredStatusText;
exports.validateConfiguredProperties = validateConfiguredProperties;
exports.validateRequiredProperties = validateRequiredProperties;
exports.toDurationDifferenceString = toDurationDifferenceString;
exports.toDurationString = toDurationString;
exports.toShortDateAndTimeString = toShortDateAndTimeString;
exports.debounce = debounce;
exports.key = key;
exports.makeResolver = makeResolver;
exports.makeResolverNoParamsWithDefaults = makeResolverNoParamsWithDefaults;
exports.makeResolverNoParams = makeResolverNoParams;
exports.WithListViewToolbarHelpers = WithListViewToolbarHelpers;
exports.createOptionalInt = createOptionalInt;
exports.optionalIntValue = optionalIntValue;
exports.optionalIntHasValue = optionalIntHasValue;
exports.useRouteData = useRouteData;
exports.WithLoader = WithLoader;
exports.WithRouteData = WithRouteData;
exports.WithRouterBase = WithRouterBase;
exports.WithRouter = WithRouter;